import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { fmtCur, fmtCurCompact, fmtDateTime } from "../lib/format";
import { toast } from "sonner";
import { ChartBar, CloudArrowUp, MagnifyingGlass, Package, Buildings, Storefront } from "@phosphor-icons/react";

// US / Walmart screen — quantities use US grouping (1,234,567), not the app-wide en-IN fmtNum.
const fmtNum = (n) => Number(n || 0).toLocaleString("en-US");

const CUSTOMER_LABELS = { walmart: "Walmart", legrand: "Legrand (WM-DI)" };

const BORDER = "1px solid var(--omh-border)";
const MUTED = "var(--omh-text-muted)";
const canEditRoles = ["admin", "super_admin", "manager", "po_dispatch"];
const th = { textAlign: "right", padding: "9px 10px", fontSize: 11.5, textTransform: "uppercase",
  letterSpacing: 0.4, color: MUTED, fontWeight: 700, whiteSpace: "nowrap", borderBottom: BORDER };
const tdBase = { padding: "8px 10px", fontSize: 13, borderBottom: BORDER, whiteSpace: "nowrap" };
const money = (v, dec = 0) => (v == null ? "—" : fmtCur(v, "USD", dec));

const COUNTRY_FLAG = { US: "US", CA: "CA" };

export default function Forecasts() {
  const { user } = useAuth();
  const canEdit = canEditRoles.includes(user?.role);
  const fileRef = useRef(null);
  const location = useLocation();
  const params = useParams();
  const customer = params.customer || "walmart";
  const customerLabel = CUSTOMER_LABELS[customer] || (customer.charAt(0).toUpperCase() + customer.slice(1));
  const tab = location.pathname.includes("order-view") ? "order" : "overview";

  const [data, setData] = useState(null);   // {vendors, weeks, order_weeks, rows}
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [groupBy, setGroupBy] = useState("item"); // item | dc
  const [deptSel, setDeptSel] = useState("all");
  const [countrySel, setCountrySel] = useState("all");
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/forecasts/walmart/consolidated?customer=${encodeURIComponent(customer)}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(() => { setDeptSel("all"); setCountrySel("all"); setQ(""); load(); /* eslint-disable-next-line */ }, [customer]);

  const doUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    const fails = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/forecasts/walmart/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const s = r.data?.snapshot;
        const all = r.data?.snapshots || [];
        ok += 1;
        if (files.length <= 3) toast.success(`Loaded ${s?.customer_label || "forecast"} · Dept ${s?.dept} · ${s?.country} — ${s?.row_count || 0} rows`);
        const lg = all.find((x) => x?.customer === "legrand" && s?.customer !== "legrand");
        if (lg) toast.info(`${lg.row_count} Legrand (WM-DI) item${lg.row_count === 1 ? "" : "s"} moved to the Legrand forecast`);
      } catch (err) {
        fails.push(`${f.name}: ${err.response?.data?.detail || "failed"}`);
      }
    }
    if (files.length > 3 && ok) toast.success(`Loaded ${ok} forecast file${ok === 1 ? "" : "s"}`);
    if (fails.length) toast.error(`${fails.length} file${fails.length === 1 ? "" : "s"} failed — ${fails[0]}`);
    load();
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const rows = data?.rows || [];
  const vendors = data?.vendors || [];
  const weeks = data?.weeks || [];
  const orderWeeks = data?.order_weeks || [];

  const depts = useMemo(() => [...new Set(rows.map((r) => r.dept).filter(Boolean))].sort((a, b) => Number(a) - Number(b)), [rows]);
  const countries = useMemo(() => [...new Set(rows.map((r) => r.country).filter(Boolean))].sort(), [rows]);
  const maxWeek = useMemo(() => Math.max(0, ...vendors.map((v) => Number(v.start_week) || 0)), [vendors]);
  const vendorByKey = useMemo(() => {
    const m = {};
    vendors.forEach((v) => { m[`${v.dept}|${v.country}`] = v; });
    return m;
  }, [vendors]);
  // Most recent upload across all sources (for the header "last upload" stamp)
  const latestUpload = useMemo(() => {
    let best = null;
    vendors.forEach((v) => { if (v.uploaded_at && (!best || v.uploaded_at > best.uploaded_at)) best = v; });
    return best;
  }, [vendors]);

  // dept/country filter (cards + tables), then search (tables only)
  const scoped = useMemo(() => rows.filter((r) =>
    (deptSel === "all" || String(r.dept) === deptSel) && (countrySel === "all" || r.country === countrySel)
  ), [rows, deptSel, countrySel]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return scoped;
    const toks = term.split(/\s+/);
    return scoped.filter((r) => {
      const hay = [r.vendor_stk, r.item_desc, r.whse, r.buyer, r.prime_item, r.dc_item,
        r.dept, r.country, r.mdse_category, r.mdse_subcategory, r.fineline, r.fineline_desc]
        .map((x) => String(x || "").toLowerCase()).join(" ");
      return toks.every((t) => hay.includes(t));
    });
  }, [scoped, q]);

  // dept roll-up cards (respect dept/country filter, ignore search)
  const deptGroups = useMemo(() => {
    const m = new Map();
    for (const r of scoped) {
      const key = `${r.dept}|${r.country}`;
      if (!m.has(key)) m.set(key, { dept: r.dept, country: r.country, items: new Set(), rows: 0,
        q4: 0, q13: 0, q26: 0, q52: 0, v4: 0, v13: 0, v26: 0, v52: 0, anyVal: false });
      const g = m.get(key);
      g.rows += 1; if (r.vendor_stk) g.items.add(r.vendor_stk);
      for (const n of [4, 13, 26, 52]) { g[`q${n}`] += r[`total_${n}`] || 0; if (r[`val_${n}`] != null) { g[`v${n}`] += r[`val_${n}`]; g.anyVal = true; } }
    }
    return [...m.values()].sort((a, b) => (Number(a.dept) - Number(b.dept)) || a.country.localeCompare(b.country));
  }, [scoped]);

  // item-level aggregation for the Overview table (key by item + country so 72-US and 72-CA stay apart)
  const itemGroups = useMemo(() => {
    const m = new Map();
    for (const r of filtered) {
      const k = `${r.vendor_stk || r.dc_item}|${r.country}`;
      if (!m.has(k)) m.set(k, {
        vendor_stk: r.vendor_stk, item_desc: r.item_desc, buyer: r.buyer, prime_item: r.prime_item,
        dept: r.dept, country: r.country, mdse_category: r.mdse_category, mdse_subcategory: r.mdse_subcategory,
        price: r.price, price_source: r.price_source, dcs: [],
        total_4: 0, total_13: 0, total_26: 0, total_52: 0, val_4: 0, val_13: 0, val_26: 0, val_52: 0,
        rush: 0, ss_backfill: 0, rush_2wk: 0, order_proj: {},
      });
      const g = m.get(k);
      g.dcs.push(r);
      for (const n of [4, 13, 26, 52]) { g[`total_${n}`] += r[`total_${n}`] || 0; g[`val_${n}`] += r[`val_${n}`] || 0; }
      g.rush += r.rush || 0; g.ss_backfill += r.ss_backfill || 0; g.rush_2wk += r.rush_2wk || 0;
      if (g.price == null && r.price != null) { g.price = r.price; g.price_source = r.price_source; }
      for (const w of Object.keys(r.order_proj || {})) g.order_proj[w] = (g.order_proj[w] || 0) + (r.order_proj[w] || 0);
    }
    return [...m.values()];
  }, [filtered]);

  const visibleOrderWeeks = useMemo(() => {
    const withData = orderWeeks.filter((w) => filtered.some((r) => (r.order_proj?.[w.code] || 0) !== 0));
    return withData.length ? withData : orderWeeks;
  }, [orderWeeks, filtered]);

  const hasData = vendors.length > 0;

  return (
    <Layout>
      <div data-testid="forecasts-page" style={{ padding: "12px 26px 22px", maxWidth: 1800, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, display: "flex", alignItems: "center", gap: 10, margin: 0, color: "var(--omh-text)" }}>
            <ChartBar size={22} weight="duotone" color="#0d9488" /> {customerLabel} Forecast
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#0d9488", background: "#f0fdfa", border: "1px solid #99f6e4", padding: "2px 9px", borderRadius: 20 }}>
              {tab === "order" ? "Order View" : "Overview"}
            </span>
          </h1>
          {canEdit && (
            <>
              <input ref={fileRef} type="file" accept=".xlsm,.xlsx" multiple style={{ display: "none" }} onChange={doUpload} data-testid="fc-upload-input" />
              <button data-testid="fc-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 15px", border: "none", borderRadius: 9,
                  background: uploading ? "#94a3b8" : "#0d9488", color: "#fff", fontWeight: 700, fontSize: 13, cursor: uploading ? "default" : "pointer" }}>
                <CloudArrowUp size={17} weight="bold" /> {uploading ? "Uploading…" : "Upload forecasts"}
              </button>
            </>
          )}
        </div>

        {loading ? (
          <div style={{ padding: 60, textAlign: "center", color: MUTED }}>Loading forecast…</div>
        ) : !hasData ? (
          <div data-testid="fc-empty" style={{ marginTop: 30, padding: 48, textAlign: "center", border: `1px dashed var(--omh-border)`, borderRadius: 14, background: "#fff" }}>
            <ChartBar size={40} weight="duotone" color="#94a3b8" />
            <div style={{ fontSize: 16, fontWeight: 700, marginTop: 12 }}>No {customerLabel} forecast loaded yet</div>
            <div style={{ color: MUTED, fontSize: 13.5, marginTop: 6 }}>
              {canEdit ? "Upload a forecast workbook (.xlsm) — the customer is detected from the filename." : "Ask an admin to upload a forecast workbook."}
            </div>
          </div>
        ) : (
          <>
            {/* Controls + sources (single compact row) */}
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
              <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 10, padding: 3 }}>
                {[["item", "By Item", Package], ["dc", "By DC", Buildings]].map(([k, lbl, Ic]) => (
                  <button key={k} data-testid={`fc-group-${k}`} onClick={() => setGroupBy(k)}
                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 13px", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer",
                      background: groupBy === k ? "#fff" : "transparent", color: groupBy === k ? "#0d9488" : MUTED,
                      boxShadow: groupBy === k ? "0 1px 3px rgba(0,0,0,.1)" : "none" }}><Ic size={15} weight="bold" /> {lbl}</button>
                ))}
              </div>
              <select data-testid="fc-dept-filter" value={deptSel} onChange={(e) => setDeptSel(e.target.value)}
                style={{ padding: "8px 11px", border: BORDER, borderRadius: 9, fontSize: 13, background: "#fff" }}>
                <option value="all">All Departments</option>
                {depts.map((d) => <option key={d} value={String(d)}>Dept {d}</option>)}
              </select>
              {countries.length > 1 && (
                <select data-testid="fc-country-filter" value={countrySel} onChange={(e) => setCountrySel(e.target.value)}
                  style={{ padding: "8px 11px", border: BORDER, borderRadius: 9, fontSize: 13, background: "#fff" }}>
                  <option value="all">US &amp; Canada</option>
                  {countries.map((c) => <option key={c} value={c}>{c === "CA" ? "Canada" : "United States"}</option>)}
                </select>
              )}
              <div style={{ position: "relative", flex: 1, minWidth: 190, maxWidth: 320 }}>
                <MagnifyingGlass size={16} style={{ position: "absolute", left: 11, top: 10, color: MUTED }} />
                <input data-testid="fc-search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search item, description, category…"
                  style={{ width: "100%", padding: "8px 12px 8px 34px", border: BORDER, borderRadius: 9, fontSize: 13, background: "#fff" }} />
              </div>
              <span data-testid="fc-sources" style={{ fontSize: 11.5, color: "#0f766e", fontWeight: 600, whiteSpace: "nowrap" }}>
                {vendors.length} dept{vendors.length === 1 ? "" : "s"} · WK{String(maxWeek).slice(4)} · {rows.length} rows
                {tab === "order" && <span style={{ color: MUTED, fontWeight: 500 }}> · Qty&amp;$ per week, empty weeks hidden</span>}
              </span>
              {latestUpload && (
                <span data-testid="fc-last-upload" style={{ fontSize: 11, color: MUTED, fontWeight: 500, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 5 }}
                  title={latestUpload.filename ? `File: ${latestUpload.filename}` : ""}>
                  <CloudArrowUp size={13} weight="bold" style={{ color: MUTED }} />
                  Last upload {latestUpload.uploaded_by ? <>by <b style={{ color: "var(--omh-text)", fontWeight: 700, marginLeft: 3 }}>{latestUpload.uploaded_by}</b></> : ""} · {fmtDateTime(latestUpload.uploaded_at)}
                </span>
              )}
            </div>

            {tab === "overview" && (deptSel === "all"
              ? <DeptCards groups={deptGroups} vendorByKey={vendorByKey} maxWeek={maxWeek} />
              : <ItemCards groups={itemGroups} />)}

            {tab === "overview" ? (
              <OverviewTab groupBy={groupBy} rows={filtered} itemGroups={itemGroups} />
            ) : (
              <OrderViewTab groupBy={groupBy} rows={filtered} itemGroups={itemGroups} weeks={visibleOrderWeeks} />
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function DeptCards({ groups, vendorByKey, maxWeek }) {
  return (
    <div data-testid="fc-dept-cards" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14, marginTop: 20 }}>
      {groups.map((g) => {
        const v = vendorByKey[`${g.dept}|${g.country}`];
        const wk = Number(v?.start_week) || 0;
        const current = wk === maxWeek;
        const accent = g.country === "CA" ? "#dc2626" : "#0d9488";
        return (
          <div key={`${g.dept}-${g.country}`} data-testid={`fc-dept-card-${g.dept}-${g.country}`}
            style={{ background: "#fff", border: BORDER, borderTop: `3px solid ${accent}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "var(--omh-text)" }}>
                Dept {g.dept} <span style={{ fontSize: 11, fontWeight: 700, color: accent, background: g.country === "CA" ? "#fef2f2" : "#f0fdfa", border: `1px solid ${g.country === "CA" ? "#fecaca" : "#99f6e4"}`, padding: "1px 7px", borderRadius: 10, marginLeft: 4 }}>{COUNTRY_FLAG[g.country] || g.country}</span>
              </div>
              <span title={v ? `Uploaded ${fmtDateTime(v.uploaded_at)}` : ""}
                style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                  color: current ? "#15803d" : "#b45309", background: current ? "#f0fdf4" : "#fffbeb", border: `1px solid ${current ? "#bbf7d0" : "#fde68a"}` }}>
                {current ? "Current" : `WK${String(wk).slice(4)} · older`}
              </span>
            </div>
            <div style={{ fontSize: 27, fontWeight: 800, marginTop: 10, fontFamily: "Bogle, 'Helvetica Neue', Arial, sans-serif", letterSpacing: "-0.01em", color: accent }}>
              {g.anyVal ? fmtCurCompact(g.v52, "USD") : "$ n/a"}<span style={{ fontSize: 11, fontWeight: 600, color: MUTED }}> / year</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: MUTED, marginTop: 2 }}>
              {fmtNum(g.q52)} <span style={{ fontSize: 11 }}>units / year</span>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 10, fontSize: 11.5, color: MUTED, flexWrap: "wrap" }}>
              <span>4w <b style={{ color: "var(--omh-text)" }}>{fmtNum(g.q4)}</b></span>
              <span>13w <b style={{ color: "var(--omh-text)" }}>{fmtNum(g.q13)}</b></span>
              <span>26w <b style={{ color: "var(--omh-text)" }}>{fmtNum(g.q26)}</b></span>
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: MUTED }}>{g.items.size} item{g.items.size === 1 ? "" : "s"} · {g.rows} rows</div>
            {v && (v.uploaded_by || v.uploaded_at) && (
              <div data-testid={`fc-dept-upload-stamp-${g.dept}-${g.country}`}
                style={{ marginTop: 8, paddingTop: 8, borderTop: BORDER, fontSize: 10.5, color: MUTED, display: "flex", alignItems: "center", gap: 5 }}
                title={v.filename ? `File: ${v.filename}` : ""}>
                <CloudArrowUp size={12} weight="bold" style={{ color: MUTED, flexShrink: 0 }} />
                <span>Uploaded {v.uploaded_by ? <>by <b style={{ color: "var(--omh-text)", fontWeight: 700 }}>{v.uploaded_by}</b> </> : ""}{v.uploaded_at ? `· ${fmtDateTime(v.uploaded_at)}` : ""}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ItemCards({ groups }) {
  const sorted = [...groups].sort((a, b) => (b.val_52 || 0) - (a.val_52 || 0));
  return (
    <div data-testid="fc-item-cards" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(232px, 1fr))", gap: 12, marginTop: 20 }}>
      {sorted.map((g, i) => {
        const accent = g.country === "CA" ? "#dc2626" : "#0d9488";
        const anyVal = g.val_52 != null && g.val_52 !== 0;
        return (
          <div key={(g.vendor_stk || "") + (g.country || "") + i} data-testid={`fc-item-card-${g.vendor_stk || i}`}
            style={{ background: "#fff", border: BORDER, borderTop: `3px solid ${accent}`, borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 800, fontFamily: "Inter, sans-serif", color: "var(--omh-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {g.vendor_stk || "—"}
                {g.dcs?.length > 1 && <span style={{ marginLeft: 6, fontSize: 10.5, color: MUTED, fontWeight: 600 }}>· {g.dcs.length} DCs</span>}
              </div>
              <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, color: accent, background: g.country === "CA" ? "#fef2f2" : "#f0fdfa", border: `1px solid ${g.country === "CA" ? "#fecaca" : "#99f6e4"}`, padding: "1px 7px", borderRadius: 10 }}>{COUNTRY_FLAG[g.country] || g.country}</span>
            </div>
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={g.item_desc || ""}>
              {g.item_desc || "—"}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 9, fontFamily: "Bogle, 'Helvetica Neue', Arial, sans-serif", letterSpacing: "-0.01em", color: accent }}>
              {anyVal ? fmtCurCompact(g.val_52, "USD") : "$ n/a"}<span style={{ fontSize: 10.5, fontWeight: 600, color: MUTED }}> / yr</span>
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: MUTED, marginTop: 1 }}>
              {fmtNum(g.total_52)} <span style={{ fontSize: 10.5 }}>units / yr</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 10, paddingTop: 9, borderTop: BORDER }}>
              {[["4w", g.total_4, g.val_4], ["13w", g.total_13, g.val_13], ["26w", g.total_26, g.val_26]].map(([lbl, qty, val]) => (
                <div key={lbl}>
                  <div style={{ fontSize: 10, color: MUTED, fontWeight: 700 }}>{lbl}</div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--omh-text)" }}>{fmtNum(qty)}</div>
                  <div style={{ fontSize: 10.5, color: "#0d9488", fontWeight: 600 }}>{val ? fmtCurCompact(val, "USD") : "—"}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function OverviewTab({ groupBy, rows, itemGroups }) {
  const data = groupBy === "item" ? itemGroups : rows;
  const dTh = { ...th, background: "#ecfeff", color: "#0e7490" };
  const dTd = { ...tdBase, textAlign: "right", background: "#f0fdfa", color: "#0d9488", fontWeight: 600 };
  const qTd = { ...tdBase, textAlign: "right" };
  return (
    <div style={{ marginTop: 18, background: "#fff", border: BORDER, borderRadius: 12, overflow: "auto" }}>
      <table data-testid="fc-overview-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 1050 }}>
        <thead style={{ background: "#f8fafc" }}>
          <tr>
            <th style={{ ...th, textAlign: "left" }}>Item</th>
            <th style={th}>Dept</th>
            {groupBy === "dc" && <th style={{ ...th, textAlign: "left" }}>DC</th>}
            <th style={{ ...th, textAlign: "left" }}>Description</th>
            <th style={th}>Price</th>
            <th style={th}>4w Qty</th><th style={dTh}>4w $</th>
            <th style={th}>13w Qty</th><th style={dTh}>13w $</th>
            <th style={th}>26w Qty</th><th style={dTh}>26w $</th>
            <th style={th}>52w Qty</th><th style={dTh}>52w $</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr><td colSpan={groupBy === "dc" ? 13 : 12} style={{ ...tdBase, textAlign: "center", color: MUTED, padding: 30 }}>No rows match.</td></tr>
          )}
          {data.map((r, i) => (
            <tr key={(r.vendor_stk || "") + (r.country || "") + (r.dc_item || i)} data-testid="fc-overview-row" style={{ background: i % 2 ? "#fbfdff" : "#fff" }}>
              <td style={{ ...tdBase, textAlign: "left", fontWeight: 700, fontFamily: "Inter, sans-serif" }}>
                {r.vendor_stk || "—"}
                {groupBy === "item" && r.dcs?.length > 1 && <span style={{ marginLeft: 7, fontSize: 11, color: MUTED, fontWeight: 600 }}>· {r.dcs.length} DCs</span>}
              </td>
              <td style={{ ...tdBase, textAlign: "right", color: MUTED }} title={r.mdse_category || undefined}>
                {r.dept || "—"}<span style={{ fontSize: 10, marginLeft: 3, color: r.country === "CA" ? "#dc2626" : "#0d9488", fontWeight: 700 }}>{r.country}</span>
              </td>
              {groupBy === "dc" && <td style={{ ...tdBase, textAlign: "left" }}><Storefront size={13} style={{ verticalAlign: -2, marginRight: 4, color: MUTED }} />{r.whse || "—"}</td>}
              <td style={{ ...tdBase, textAlign: "left", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }}>{r.item_desc || "—"}</td>
              <td style={{ ...tdBase, textAlign: "right", color: r.price == null ? MUTED : "var(--omh-text)" }}>
                {r.price == null ? "—" : fmtCur(r.price, "USD")}
                {r.price_source === "po" && r.price != null && <span title="PO-price fallback (no transfer price set)" style={{ fontSize: 10, color: "#d97706", marginLeft: 4 }}>PO</span>}
              </td>
              <td style={qTd}>{fmtNum(r.total_4)}</td>
              <td style={dTd}>{money(r.val_4)}</td>
              <td style={qTd}>{fmtNum(r.total_13)}</td>
              <td style={dTd}>{money(r.val_13)}</td>
              <td style={qTd}>{fmtNum(r.total_26)}</td>
              <td style={dTd}>{money(r.val_26)}</td>
              <td style={{ ...qTd, fontWeight: 700 }}>{fmtNum(r.total_52)}</td>
              <td style={{ ...dTd, fontWeight: 800 }}>{money(r.val_52)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrderViewTab({ groupBy, rows, itemGroups, weeks }) {
  const data = groupBy === "item" ? itemGroups : rows;
  const weekTotalQty = (r) => weeks.reduce((s, w) => s + (r.order_proj?.[w.code] || 0), 0);
  const dollar = (qty, price) => (price != null && qty ? qty * price : null);
  const qtyCell = { ...tdBase, textAlign: "right" };
  const dolCell = { ...tdBase, textAlign: "right", background: "#f0fdfa", color: "#0d9488", fontSize: 12 };
  const rushHead = { ...th, background: "#fef9c3" };
  // Fixed widths for the two sticky Total columns so large values never clip. The Qty column's
  // right offset must equal the $ column's width (they stack against the right edge).
  const TOT_DOL_W = 110, TOT_QTY_W = 92;
  const totQtyCol = { minWidth: TOT_QTY_W, width: TOT_QTY_W, position: "sticky", right: TOT_DOL_W };
  const totDolCol = { minWidth: TOT_DOL_W, width: TOT_DOL_W, position: "sticky", right: 0 };
  // Left freeze block: Item · Dept · Description stay visible on horizontal scroll. Each sticky
  // column needs a FIXED width + left offset so they stack cleanly, and an opaque background so the
  // scrolling columns don't bleed through.
  const ITEM_W = 112, DEPT_W = 58, DESC_W = 210;
  const DEPT_L = ITEM_W, DESC_L = ITEM_W + DEPT_W;
  const FREEZE_EDGE = "2px solid #e2e8f0";
  return (
    <div style={{ marginTop: 18, background: "#fff", border: BORDER, borderRadius: 12, overflow: "auto" }}>
      {weeks.length === 0 && <div style={{ padding: 24, color: MUTED, fontSize: 13 }}>No Order-View weeks in this selection.</div>}
      {weeks.length > 0 && (
        <div data-testid="fc-order-note" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#fffbeb", borderBottom: "1px solid #fde68a", fontSize: 12, color: "#92400e" }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: "#fef08a", border: "1px solid #fde68a", display: "inline-block" }} />
          <span><b>Rush</b>, <b>SS-Fill</b> and <b>Rush 2w</b> (yellow) are separate one-time add-on quantities from Walmart's Order View — they are <b>not</b> included in the weekly buckets or the Total.</span>
        </div>
      )}
      <table data-testid="fc-order-table" style={{ borderCollapse: "collapse", minWidth: 900 }}>
        <thead style={{ background: "#f8fafc" }}>
          <tr>
            <th rowSpan={2} style={{ ...th, textAlign: "left", position: "sticky", left: 0, minWidth: ITEM_W, width: ITEM_W, background: "#f8fafc", zIndex: 4, verticalAlign: "bottom" }}>Item{groupBy === "dc" ? " · DC" : ""}</th>
            <th rowSpan={2} style={{ ...th, position: "sticky", left: DEPT_L, minWidth: DEPT_W, width: DEPT_W, background: "#f8fafc", zIndex: 4, verticalAlign: "bottom" }}>Dept</th>
            <th rowSpan={2} style={{ ...th, textAlign: "left", position: "sticky", left: DESC_L, minWidth: DESC_W, width: DESC_W, background: "#f8fafc", zIndex: 4, borderRight: FREEZE_EDGE, verticalAlign: "bottom" }}>Description</th>
            <th rowSpan={2} style={{ ...rushHead, verticalAlign: "bottom" }} title="Rush order 2Wks+ prior">Rush</th>
            <th rowSpan={2} style={{ ...rushHead, verticalAlign: "bottom" }} title="Store SS Back Fill Qty">SS Fill</th>
            <th rowSpan={2} style={{ ...rushHead, verticalAlign: "bottom" }} title="Rush order within 2 Wks">Rush 2w</th>
            {weeks.map((w) => (
              <th key={w.code} colSpan={2} style={{ ...th, textAlign: "center", borderLeft: BORDER }}>
                <div style={{ fontWeight: 800, color: "var(--omh-text)" }}>WK{String(w.code).slice(4)}</div>
                <div style={{ fontSize: 10, fontWeight: 500, color: MUTED, textTransform: "none", letterSpacing: 0 }}>{w.label?.replace(/ \d{4}$/, "")}</div>
              </th>
            ))}
            <th colSpan={2} style={{ ...th, textAlign: "center", position: "sticky", right: 0, background: "#ecfdf5", zIndex: 3, borderLeft: "2px solid #99f6e4" }}>Total</th>
          </tr>
          <tr>
            {weeks.flatMap((w) => [
              <th key={w.code + "q"} style={{ ...th, textAlign: "right", minWidth: 60, borderLeft: BORDER, fontSize: 10, paddingTop: 3, paddingBottom: 3 }}>Qty</th>,
              <th key={w.code + "$"} style={{ ...th, textAlign: "right", minWidth: 66, color: "#0e7490", background: "#ecfeff", fontSize: 10, paddingTop: 3, paddingBottom: 3 }}>$</th>,
            ])}
            <th style={{ ...th, textAlign: "right", ...totQtyCol, background: "#ecfdf5", zIndex: 3, fontSize: 10, paddingTop: 3, paddingBottom: 3, borderLeft: "2px solid #99f6e4" }}>Qty</th>
            <th style={{ ...th, textAlign: "right", ...totDolCol, background: "#ecfeff", color: "#0e7490", zIndex: 3, fontSize: 10, paddingTop: 3, paddingBottom: 3 }}>$</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr><td colSpan={7 + weeks.length * 2 + 2} style={{ ...tdBase, textAlign: "center", color: MUTED, padding: 30 }}>No rows match.</td></tr>
          )}
          {data.map((r, i) => {
            const bg = i % 2 ? "#fbfdff" : "#fff";
            const totQty = weekTotalQty(r);
            return (
              <tr key={(r.vendor_stk || "") + (r.country || "") + (r.dc_item || i)} data-testid="fc-order-row" style={{ background: bg }}>
                <td style={{ ...tdBase, textAlign: "left", fontWeight: 700, fontFamily: "Inter, sans-serif", position: "sticky", left: 0, minWidth: ITEM_W, width: ITEM_W, background: bg, zIndex: 2 }}>
                  {r.vendor_stk || "—"}{groupBy === "dc" && <span style={{ color: MUTED, fontWeight: 600 }}> · {r.whse}</span>}
                </td>
                <td style={{ ...qtyCell, color: MUTED, position: "sticky", left: DEPT_L, minWidth: DEPT_W, width: DEPT_W, background: bg, zIndex: 2, textAlign: "left" }}>{r.dept}<span style={{ fontSize: 10, marginLeft: 2, color: r.country === "CA" ? "#dc2626" : "#0d9488", fontWeight: 700 }}>{r.country}</span></td>
                <td style={{ ...tdBase, textAlign: "left", position: "sticky", left: DESC_L, minWidth: DESC_W, width: DESC_W, maxWidth: DESC_W, background: bg, zIndex: 2, borderRight: FREEZE_EDGE, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.item_desc || "—"}</td>
                <td style={{ ...qtyCell, background: "#fefce8" }}>{r.rush ? fmtNum(r.rush) : "—"}</td>
                <td style={{ ...qtyCell, background: "#fefce8" }}>{r.ss_backfill ? fmtNum(r.ss_backfill) : "—"}</td>
                <td style={{ ...qtyCell, background: "#fefce8" }}>{r.rush_2wk ? fmtNum(r.rush_2wk) : "—"}</td>
                {weeks.map((w) => {
                  const qty = r.order_proj?.[w.code] || 0;
                  return [
                    <td key={w.code} style={{ ...qtyCell, borderLeft: BORDER, color: qty ? "var(--omh-text)" : "#cbd5e1" }}>{qty ? fmtNum(qty) : "·"}</td>,
                    <td key={w.code + "$"} style={{ ...dolCell, color: qty && r.price != null ? "#0d9488" : "#cbd5e1" }}>{qty ? money(dollar(qty, r.price)) : "·"}</td>,
                  ];
                })}
                <td style={{ ...qtyCell, ...totQtyCol, fontWeight: 800, background: "#f0fdf4", color: "#15803d", zIndex: 2, borderLeft: "2px solid #99f6e4" }}>{fmtNum(totQty)}</td>
                <td style={{ ...dolCell, ...totDolCol, fontWeight: 800, background: "#ecfdf5", color: "#0d9488", zIndex: 2 }}>{money(dollar(totQty, r.price))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
